import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { BarChart3, Building2, Search, Users, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { AdminInvitationCodeListResponse } from "@/server/admin";
import { listAdminInvitationCodesServer } from "@/server/admin";

interface SearchParams {
  page?: number;
  query?: string;
  codeId?: string;
}

const PAGE_SIZE = 20;

export const Route = createFileRoute("/admin/invitation-codes")({
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    page: Number(search.page) || 1,
    query: typeof search.query === "string" ? search.query : undefined,
    codeId: typeof search.codeId === "string" ? search.codeId : undefined,
  }),
  loaderDeps: ({ search }) => ({
    page: search.page || 1,
    query: search.query,
  }),
  loader: async ({ deps }) => {
    const skip = ((deps.page || 1) - 1) * PAGE_SIZE;
    const list = await listAdminInvitationCodesServer({
      data: {
        skip,
        limit: PAGE_SIZE,
        query: deps.query,
      },
    });

    return {
      list: list as AdminInvitationCodeListResponse,
    };
  },
  component: AdminInvitationCodesPage,
});

function AdminInvitationCodesPage() {
  const { t } = useTranslation("admin");
  const { list } = Route.useLoaderData();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const currentPage = search.page || 1;
  const currentQuery = search.query || "";
  const currentCodeId = search.codeId;
  const [queryInput, setQueryInput] = useState(currentQuery);

  const totalPages = useMemo(() => {
    if (!list.total) return 1;
    return Math.max(1, Math.ceil(list.total / PAGE_SIZE));
  }, [list.total]);

  useEffect(() => {
    if (currentCodeId || list.items.length === 0) return;
    navigate({
      search: {
        page: currentPage,
        query: currentQuery || undefined,
        codeId: list.items[0]?.id,
      },
      replace: true,
    });
  }, [currentCodeId, currentPage, currentQuery, list.items, navigate]);

  const selectedCode = useMemo(() => {
    if (!currentCodeId) return null;
    return list.items.find((item) => item.id === currentCodeId) ?? null;
  }, [currentCodeId, list.items]);

  const handleSearch = (event: FormEvent) => {
    event.preventDefault();
    navigate({
      search: {
        page: 1,
        query: queryInput.trim() || undefined,
        codeId: undefined,
      },
    });
  };

  const handleClearSearch = () => {
    setQueryInput("");
    navigate({
      search: {
        page: 1,
        query: undefined,
        codeId: undefined,
      },
    });
  };

  const handlePageChange = (nextPage: number) => {
    const safePage = Math.min(Math.max(1, nextPage), totalPages);
    navigate({
      search: {
        page: safePage,
        query: currentQuery || undefined,
        codeId: undefined,
      },
    });
  };

  const handleSelectCode = (codeId: string) => {
    navigate({
      search: {
        page: currentPage,
        query: currentQuery || undefined,
        codeId,
      },
    });
  };

  return (
    <div className="grid h-full min-h-0 grid-cols-1 lg:grid-cols-[420px_minmax(0,1fr)]">
      <aside className="flex h-full min-h-0 flex-col overflow-hidden border-b bg-muted/20 lg:border-b-0 lg:border-r">
        <div className="flex shrink-0 items-center justify-between border-b px-5 py-4">
          <div className="space-y-0.5">
            <div className="text-sm font-medium">{t("invitationCodes.sidebar.title")}</div>
            <div className="text-xs text-muted-foreground">
              {t("invitationCodes.sidebar.total", { total: list.total })} •{" "}
              {t("invitationCodes.sidebar.pageInfo", { current: currentPage, total: totalPages })}
            </div>
          </div>
          <Badge variant="secondary" className="shrink-0">
            {list.items.length}
          </Badge>
        </div>

        <div className="shrink-0 border-b px-5 py-3">
          <form onSubmit={handleSearch} className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/70" />
            <Input
              value={queryInput}
              onChange={(event) => setQueryInput(event.target.value)}
              placeholder={t("invitationCodes.sidebar.searchPlaceholder")}
              className="h-9 pl-8 pr-8"
            />
            {queryInput && (
              <button
                type="button"
                onClick={handleClearSearch}
                className="absolute inset-y-0 right-0 flex items-center pr-2 text-muted-foreground/70 hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </form>
        </div>

        <div className="min-h-0 flex-1 overflow-auto">
          {list.items.length === 0 ? (
            <div className="flex h-full items-center justify-center px-4 py-12 text-sm text-muted-foreground">
              {t("invitationCodes.sidebar.empty")}
            </div>
          ) : (
            <ul className="divide-y">
              {list.items.map((item) => {
                const isSelected = item.id === currentCodeId;
                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => handleSelectCode(item.id)}
                      aria-pressed={isSelected}
                      className={[
                        "relative w-full text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                        isSelected ? "bg-muted/60" : "hover:bg-muted/40",
                      ].join(" ")}
                    >
                      <span
                        aria-hidden="true"
                        className={[
                          "absolute inset-y-2 left-0 w-0.5 rounded-full bg-primary transition-opacity",
                          isSelected ? "opacity-100" : "opacity-0",
                        ].join(" ")}
                      />
                      <div className="space-y-2 px-5 py-4">
                        <div className="flex items-center justify-between gap-2">
                          <div className="truncate text-sm font-medium">{item.code}</div>
                          <span
                            className={[
                              "inline-flex h-7 items-center rounded-md border px-2.5 text-xs font-medium",
                              isSelected
                                ? "border-transparent bg-secondary text-secondary-foreground"
                                : "border-border bg-background text-foreground",
                            ].join(" ")}
                          >
                            {isSelected
                              ? t("invitationCodes.sidebar.selected")
                              : t("invitationCodes.sidebar.view")}
                          </span>
                        </div>
                        <div className="truncate text-xs text-muted-foreground">
                          {item.workspace_slug || item.workspace_name || "-"}
                        </div>
                        <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            {item.register_count}
                          </span>
                          <span className="flex items-center gap-1">
                            <BarChart3 className="h-3 w-3" />
                            {item.activated_count}/{item.register_count}
                          </span>
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="flex shrink-0 items-center justify-between border-t px-5 py-4">
          <div className="text-xs text-muted-foreground">
            {t("invitationCodes.sidebar.showing", { count: list.items.length })}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage <= 1}
            >
              {t("invitationCodes.sidebar.previous")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= totalPages || list.items.length < PAGE_SIZE}
            >
              {t("invitationCodes.sidebar.next")}
            </Button>
          </div>
        </div>
      </aside>

      <main className="min-h-0 min-w-0 overflow-auto bg-background px-4 py-4 lg:px-6 lg:py-5">
        {!selectedCode ? (
          <div className="flex min-h-[60vh] items-center justify-center rounded-xl border border-dashed text-sm text-muted-foreground">
            {t("invitationCodes.detail.selectPrompt")}
          </div>
        ) : (
          <div className="space-y-6">
            <header className="space-y-3 border-b pb-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="space-y-1.5">
                  <h2 className="text-2xl font-semibold tracking-tight">{selectedCode.code}</h2>
                  <div className="text-sm text-muted-foreground">
                    {t("invitationCodes.detail.created")}: {formatDate(selectedCode.created_at)}
                  </div>
                </div>
                <Badge variant="outline">{t("invitationCodes.detail.activationRateBadge")}</Badge>
              </div>

              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                <MetricCard
                  icon={<Users className="h-4 w-4" />}
                  label={t("invitationCodes.detail.registerCount")}
                  value={String(selectedCode.register_count)}
                />
                <MetricCard
                  icon={<BarChart3 className="h-4 w-4" />}
                  label={t("invitationCodes.detail.activatedCount")}
                  value={String(selectedCode.activated_count)}
                />
                <MetricCard
                  icon={<BarChart3 className="h-4 w-4" />}
                  label={t("invitationCodes.detail.nonActivatedCount")}
                  value={String(selectedCode.non_activated_count)}
                />
                <MetricCard
                  icon={<BarChart3 className="h-4 w-4" />}
                  label={t("invitationCodes.detail.activationRate")}
                  value={`${selectedCode.activation_rate.toFixed(2)}%`}
                />
                <MetricCard
                  icon={<Building2 className="h-4 w-4" />}
                  label={t("invitationCodes.detail.workspace")}
                  value={
                    selectedCode.workspace_id ? (
                      <Link
                        to="/admin/workspaces"
                        search={{ workspaceId: selectedCode.workspace_id }}
                        className="text-primary hover:underline"
                      >
                        {selectedCode.workspace_name ||
                          selectedCode.workspace_slug ||
                          selectedCode.workspace_id}
                      </Link>
                    ) : (
                      "-"
                    )
                  }
                />
              </div>
            </header>

            <section className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-medium">
                  {t("invitationCodes.detail.dailyRegistrations")}
                </h3>
                <span className="text-xs text-muted-foreground">
                  {t("invitationCodes.detail.dailyRows", {
                    count: selectedCode.daily_registrations.length,
                  })}
                </span>
              </div>

              <div className="overflow-hidden rounded-xl border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("invitationCodes.table.date")}</TableHead>
                      <TableHead className="text-right">
                        {t("invitationCodes.table.registrations")}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedCode.daily_registrations.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={2} className="text-center text-muted-foreground">
                          {t("invitationCodes.detail.dailyEmpty")}
                        </TableCell>
                      </TableRow>
                    ) : (
                      selectedCode.daily_registrations.map((row) => (
                        <TableRow key={`${selectedCode.id}-${row.date}`}>
                          <TableCell>{formatDate(row.date)}</TableCell>
                          <TableCell className="text-right font-medium">{row.count}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </section>
          </div>
        )}
      </main>
    </div>
  );
}

function MetricCard({
  label,
  value,
  icon,
}: {
  label: string;
  value: string | ReactNode;
  icon: ReactNode;
}) {
  return (
    <div className="rounded-xl border bg-muted/20 p-4">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        {icon}
        <span>{label}</span>
      </div>
      <div className="mt-2 break-all text-lg font-semibold">{value}</div>
    </div>
  );
}

function formatDate(value: string | null | undefined): string {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
}
